import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../components/AdminLayout";
import { createArticle, uploadFile, getCategories } from "../../lib/api";
import { toast } from "sonner";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { 
  ArrowLeft, 
  Upload, 
  Calendar as CalendarIcon,
  Save,
  Eye,
  Image as ImageIcon
} from "lucide-react";

const CATEGORIES = ["Notícias", "Shows", "Lançamentos", "Artistas", "Top Hits"];

export default function CreateArticle() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(null);
  const [form, setForm] = useState({
    title: "",
    excerpt: "",
    content: "",
    category: "Notícias",
    image_url: "",
    source_url: "",
    source_name: "",
    status: "draft",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadFile(file);
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${response.data.path}`;
      setForm(prev => ({ ...prev, image_url: imageUrl }));
      toast.success("Imagem enviada com sucesso!");
    } catch (error) {
      toast.error("Erro ao enviar imagem");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (status) => {
    if (!form.title || !form.content) {
      toast.error("Preencha o título e o conteúdo");
      return;
    }

    setLoading(true);
    try {
      const data = {
        ...form,
        status,
        scheduled_at: status === "scheduled" && scheduledDate 
          ? scheduledDate.toISOString() 
          : null,
        content: `<p>${form.content.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>')}</p>`,
      };

      await createArticle(data);
      toast.success(
        status === "published" 
          ? "Matéria publicada!" 
          : status === "scheduled"
          ? "Matéria agendada!"
          : "Rascunho salvo!"
      );
      navigate("/admin/articles");
    } catch (error) {
      toast.error("Erro ao salvar matéria");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/admin/articles")}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={24} className="text-zinc-400" />
          </button>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white" data-testid="create-article-title">
              Nova Matéria
            </h1>
            <p className="text-zinc-500 mt-1">Crie uma nova matéria para o portal</p>
          </div>
        </div>

        {/* Form */}
        <div className="bg-[#121212] rounded-2xl border border-white/10 p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">
              Título *
            </label>
            <input
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF007F] transition-colors"
              placeholder="Digite o título da matéria"
              data-testid="title-input"
            />
          </div>

          {/* Excerpt */}
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">
              Resumo
            </label>
            <textarea
              name="excerpt"
              value={form.excerpt}
              onChange={handleChange}
              rows={2}
              className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF007F] transition-colors resize-none"
              placeholder="Um breve resumo da matéria"
              data-testid="excerpt-input"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">
              Conteúdo *
            </label>
            <textarea
              name="content"
              value={form.content}
              onChange={handleChange}
              rows={12}
              className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF007F] transition-colors resize-none"
              placeholder="Digite o conteúdo completo da matéria..."
              data-testid="content-input"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">
              Categoria
            </label>
            <select
              name="category"
              value={form.category}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white appearance-none cursor-pointer focus:outline-none focus:border-[#FF007F] transition-colors"
              data-testid="category-select"
            >
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">
              Imagem de Capa
            </label>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-zinc-400 hover:border-[#FF007F] cursor-pointer transition-colors">
                {uploading ? (
                  <div className="w-5 h-5 border-2 border-[#FF007F] border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Upload size={20} />
                )}
                <span>{uploading ? "Enviando..." : "Upload"}</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="image-upload"
                />
              </label>
              <span className="text-zinc-600">ou</span>
              <input
                type="url"
                name="image_url"
                value={form.image_url}
                onChange={handleChange}
                className="flex-1 px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF007F] transition-colors"
                placeholder="URL da imagem"
                data-testid="image-url-input"
              />
            </div>
            {form.image_url && (
              <div className="mt-4 relative rounded-xl overflow-hidden">
                <img 
                  src={form.image_url} 
                  alt="Preview" 
                  className="w-full h-48 object-cover"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}
          </div>

          {/* Source */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Nome da Fonte
              </label>
              <input
                type="text"
                name="source_name"
                value={form.source_name}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF007F] transition-colors"
                placeholder="Ex: Portal do Arrocha"
                data-testid="source-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                URL da Fonte
              </label>
              <input
                type="url"
                name="source_url"
                value={form.source_url}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF007F] transition-colors"
                placeholder="https://..."
                data-testid="source-url-input"
              />
            </div>
          </div>

          {/* Schedule */}
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-2">
              Agendar Publicação
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="flex items-center gap-2 px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-zinc-400 hover:border-[#FF007F] transition-colors"
                  data-testid="schedule-btn"
                >
                  <CalendarIcon size={20} />
                  {scheduledDate 
                    ? format(scheduledDate, "PPP", { locale: ptBR })
                    : "Selecionar data"
                  }
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[#121212] border-white/10" align="start">
                <Calendar
                  mode="single"
                  selected={scheduledDate}
                  onSelect={setScheduledDate}
                  locale={ptBR}
                  className="rounded-xl"
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-end">
          <button
            onClick={() => handleSubmit("draft")}
            disabled={loading}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-medium rounded-xl transition-all disabled:opacity-50"
            data-testid="save-draft-btn"
          >
            <Save size={20} />
            Salvar Rascunho
          </button>
          {scheduledDate && (
            <button
              onClick={() => handleSubmit("scheduled")}
              disabled={loading}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-xl transition-all disabled:opacity-50"
              data-testid="schedule-submit-btn"
            >
              <CalendarIcon size={20} />
              Agendar
            </button>
          )}
          <button
            onClick={() => handleSubmit("published")}
            disabled={loading}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-[#FF007F] hover:bg-[#FF3399] text-white font-bold rounded-xl transition-all disabled:opacity-50"
            data-testid="publish-btn"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <Eye size={20} />
                Publicar
              </>
            )}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
